import type { OrderListItemDTO } from '@wf/shared'
import { minToTime } from '../orders/schedule'
import { PlanCard } from './PlanCard'
import type { PlanDay } from './planung'

/** Pixel je Stunde — fixe Raster-Dichte; muss zum CSS-Stundenraster (.wf-plan__body) passen. */
export const PX_PER_HOUR = 64
const MIN_CARD_PX = 26

/**
 * Das Belegungs-Raster (Redesign 15.07., token-treu nach Simons Claude-Design-Entwurf): EIN geschlossenes
 * Panel mit innenliegender Stunden-Spalte + Platz-Spalten (Trennlinien statt Einzelrahmen), rechts das
 * eigenständige „Nicht eingeplant"-Panel. Karten absolut positioniert (React-CSSOM, CSP-konform); eine
 * rote „Jetzt"-Linie markiert die aktuelle Uhrzeit (nur heute, nur im Öffnungsfenster). Slots außerhalb
 * des Fensters werden sichtbar an die Kante geklemmt statt zu verschwinden.
 */
export function PlanGrid({
  plan,
  backlog,
  openStartMin,
  openEndMin,
  nowMin,
  showNow,
  onSelect,
}: {
  plan: PlanDay
  backlog: readonly OrderListItemDTO[]
  openStartMin: number
  openEndMin: number
  /** Aktuelle Uhrzeit (Minuten ab Mitternacht) — Position der „Jetzt"-Linie. */
  nowMin: number
  /** „Jetzt"-Linie nur am HEUTIGEN Tag zeigen. */
  showNow: boolean
  onSelect: (order: OrderListItemDTO) => void
}) {
  const windowMin = Math.max(60, openEndMin - openStartMin)
  const bodyPx = (windowMin / 60) * PX_PER_HOUR
  // Volle Stunden im Fenster (inkl. Endstunde) für Achsen-Beschriftung + Rasterlinien.
  const hours: number[] = []
  for (let m = Math.ceil(openStartMin / 60) * 60; m <= openEndMin; m += 60) hours.push(m)
  const nowVisible = showNow && nowMin >= openStartMin && nowMin <= openEndMin
  const nowTop = ((nowMin - openStartMin) / 60) * PX_PER_HOUR

  const cardStyle = (startMin: number, endMin: number, lane: number, laneCount: number): React.CSSProperties => {
    const top = ((Math.max(startMin, openStartMin) - openStartMin) / 60) * PX_PER_HOUR
    const height = ((Math.min(endMin, openEndMin) - Math.max(startMin, openStartMin)) / 60) * PX_PER_HOUR
    return {
      top: Math.min(Math.max(0, top), bodyPx - MIN_CARD_PX),
      height: Math.max(MIN_CARD_PX, height),
      left: `${(lane / laneCount) * 100}%`,
      width: `calc(${100 / laneCount}% - 8px)`,
    }
  }

  return (
    // role=group statt grid: das Layout ist kein interaktives ARIA-Grid-Widget (kein Zellen-Fokusmodell) —
    // grid>gridcell ohne row wäre ein invalider Accessibility-Tree (Review-Fund 15.07.).
    <div className="wf-plan" role="group" aria-label="Belegungs-Plantafel">
      <div className="wf-plan__panel">
        <div className="wf-plan__axis" aria-hidden="true">
          {/* Unsichtbares Count-Badge = identisches höchstes Element wie in den Spalten-Köpfen → beide Köpfe
              sind font-metrik-unabhängig EXAKT gleich hoch (Review-Fund: &nbsp; erbte 16px-Body-Font und
              erzeugte 1–3px Versatz der Header-Naht + aller Stundenlabels). Kein px-min-height (Token-Guard). */}
          <div className="wf-plan__colhead">
            <span className="wf-plan__coltitle">&nbsp;</span>
            <span className="wf-plan__count" style={{ visibility: 'hidden' }}>0</span>
          </div>
          <div className="wf-plan__axisbody" style={{ height: bodyPx }}>
            {hours.map((m) => (
              <span key={m} className="wf-plan__hour" style={{ top: ((m - openStartMin) / 60) * PX_PER_HOUR }}>
                {minToTime(m)}
              </span>
            ))}
          </div>
        </div>

        {plan.byBay.map((placed, i) => (
          <div key={i} className="wf-plan__col" role="group" aria-label={`Platz ${i + 1}`}>
            <div className="wf-plan__colhead">
              <span className="wf-plan__coltitle">Platz {i + 1}</span>
              <span className="wf-plan__count">{placed.length}</span>
            </div>
            <div className="wf-plan__body" style={{ height: bodyPx }}>
              {nowVisible ? <div className="wf-plan__now" style={{ top: nowTop }} aria-hidden="true" /> : null}
              {placed.map((p) => {
                const s = cardStyle(p.startMin, p.endMin, p.lane, p.laneCount)
                // Kurz-Slot → einzeilige Kompakt-Karte, sonst schiebt die Chip-Zeile den Titel aus der Slot-Höhe.
                return <PlanCard key={p.order.id} order={p.order} onSelect={onSelect} style={s} compact={(s.height as number) < 48} />
              })}
            </div>
          </div>
        ))}
      </div>

      <aside className="wf-plan__pool" role="group" aria-label="Nicht eingeplant">
        <div className="wf-plan__poolhead">
          <span className="wf-plan__coltitle">Nicht eingeplant</span>
          <span className={`wf-plan__count${plan.unplaced.length + backlog.length > 0 ? ' wf-plan__count--warn' : ''}`}>
            {plan.unplaced.length + backlog.length}
          </span>
        </div>
        <div className="wf-plan__poolbody">
          {plan.unplaced.map((o) => (
            <PlanCard key={o.id} order={o} onSelect={onSelect} />
          ))}
          {backlog.length > 0 ? <div className="wf-plan__pooldivider">Ohne Termin</div> : null}
          {backlog.map((o) => (
            <PlanCard key={o.id} order={o} onSelect={onSelect} />
          ))}
          {plan.unplaced.length + backlog.length === 0 ? (
            <div className="wf-col__empty">Alles eingeplant</div>
          ) : (
            <div className="wf-plan__poolhint">Karte antippen, um Tag, Zeit und Platz zu vergeben</div>
          )}
        </div>
      </aside>
    </div>
  )
}
